import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, ArrowRight, ArrowUpRight, Globe, LockKeyhole, ShieldCheck } from "lucide-react";
import AuctionDocuments from "./AuctionDocuments";
import { useAuth } from "../../contexts/AuthContext";

const base_url = process.env.REACT_APP_BASE_URL;

const COPY = {
  private: {
    kicker: "Private collector access",
    title: ["Exceptional pieces.", "Personally invited."],
    story: "Enter a discreet world of exclusive collections, reserved for invited collectors.",
    formTitle: "Private auction login",
    formCopy: "Sign in with your invited collector account.",
    submit: "Login to private auctions",
    registerPrompt: "Need private access?",
    registerLabel: "Request an invitation",
    notice:
      "Private auctions are available to approved, invited accounts. Signing in does not automatically grant access.",
  },
  public: {
    kicker: "A world of discovery",
    title: ["Your next discovery", "begins here."],
    story: "Discover rare works and timeless treasures through our public auctions.",
    formTitle: "Public auction login",
    formCopy: "Sign in to explore the catalogue and join public auctions.",
    submit: "Login to public auctions",
    registerPrompt: "New to Anachronistic?",
    registerLabel: "Create an account",
    notice:
      "A registered account is required to access public auctions. Bidding is subject to auction registration and approval.",
  },
};

const openAuthModal = (type) => window.dispatchEvent(new CustomEvent("openAuthModal", { detail: type }));

const AuctionAccess = () => {
  const [screen, setScreen] = useState("home");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const go = (next) => {
    setScreen(next);
    setEmail("");
    setPassword("");
    setShowPassword(false);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${base_url}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(
          data.message === "Request pending approval"
            ? "Your account is pending approval. Please wait for admin confirmation."
            : data.message || "Login failed"
        );
        setPassword("");
        return;
      }
      await login(data.token);
      toast.success("Login successful!");
      if (screen === "private") navigate("/privateauctionview");
    } catch (err) {
      console.error("Login error:", err);
      setError("Something went wrong. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  if (screen === "home") {
    return (
      <section className="aah-access">
        <main>
          <div className="eyebrow">The world of exceptional collecting</div>
          <h1>
            Rare masterpieces.
            <br />
            Extraordinary possibilities.
          </h1>
          <p className="intro">
            Discover timeless treasures and exclusive collections. Choose your auction experience to continue.
          </p>
          <div className="auction-tabs" aria-label="Auction access">
            <button type="button" onClick={() => go("private")}>
              <LockKeyhole aria-hidden="true" />
              Private Auction
            </button>
            <button type="button" onClick={() => go("public")}>
              <Globe aria-hidden="true" />
              Public Auction
            </button>
          </div>
          <div className="choices">
            <article className="choice">
              <div className="type">
                <LockKeyhole aria-hidden="true" />
                <span className="tag">By invitation</span>
              </div>
              <h2>Private Auction</h2>
              <p>A discreet setting for invited collectors to explore exclusive offerings and exceptional pieces.</p>
              <button type="button" className="action" onClick={() => go("private")}>
                Login to private auctions
                <ArrowUpRight aria-hidden="true" />
              </button>
            </article>
            <article className="choice">
              <div className="type">
                <Globe aria-hidden="true" />
                <span className="tag">Open to registered collectors</span>
              </div>
              <h2>Public Auction</h2>
              <p>Explore our public auction catalogue and take part in the pursuit of your next remarkable discovery.</p>
              <button type="button" className="action" onClick={() => go("public")}>
                Login to public auctions
                <ArrowUpRight aria-hidden="true" />
              </button>
            </article>
          </div>
          <p className="fine">
            <ShieldCheck aria-hidden="true" />
            Login is required for both private and public auctions.
          </p>
          <AuctionDocuments />
        </main>
      </section>
    );
  }

  const c = COPY[screen];

  return (
    <section className="aah-access">
      <div className="login-layout">
        <aside className="story">
          <button type="button" className="back" onClick={() => go("home")}>
            <ArrowLeft aria-hidden="true" />
            Back to auctions
          </button>
          <div className="eyebrow">{c.kicker}</div>
          <h1>
            {c.title[0]}
            <br />
            {c.title[1]}
          </h1>
          <p>{c.story}</p>
          <div className="seal">
            <ShieldCheck aria-hidden="true" />
            ANACHRONISTIC AUCTION HOUSE
          </div>
        </aside>
        <div className="form-area">
          <div className="mode" aria-label="Choose auction login">
            {["private", "public"].map((key) => (
              <button
                key={key}
                type="button"
                className={screen === key ? "active" : ""}
                aria-pressed={screen === key}
                onClick={() => go(key)}
              >
                {key === "private" ? "Private Auction" : "Public Auction"}
              </button>
            ))}
          </div>
          <h2>{c.formTitle}</h2>
          <p className="sub">{c.formCopy}</p>
          <form onSubmit={handleSubmit}>
            <label className="field" htmlFor="aah-email">Email address</label>
            <input
              id="aah-email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <label className="field" htmlFor="aah-password">Password</label>
            <div className="password">
              <input
                id="aah-password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="button" className="show" onClick={() => setShowPassword((s) => !s)}>
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <div className="form-row">
              <span />
              <button type="button" className="textlink" onClick={() => openAuthModal("login")}>
                Forgot password?
              </button>
            </div>
            <button type="submit" className="action" disabled={submitting}>
              <span>{submitting ? "Signing in..." : c.submit}</span>
              <ArrowRight aria-hidden="true" />
            </button>
          </form>
          <div className="registration">
            {c.registerPrompt}{" "}
            <button
              type="button"
              className="textlink"
              onClick={() => (screen === "public" ? openAuthModal("signup") : openAuthModal("login"))}
            >
              {c.registerLabel}
            </button>
          </div>
          <div className="notice">{c.notice}</div>
          {error && <p className="feedback error" role="alert">{error}</p>}
        </div>
      </div>
    </section>
  );
};

export default AuctionAccess;
